/* eslint complexity: ["error", {max: 12}] -- register/edit/unregister sheet orchestration */
/* eslint max-lines: ["error", {max: 265}] -- register/edit/unregister sheet orchestration */
/* eslint max-lines-per-function: ["error", {max: 175}] -- register/edit/unregister sheet orchestration */
import { useState } from "react";

import isNil from "lodash/isNil";

import type { GroupClassSeriesAnalyticsSources } from "@/constants/analytics/Analytics";
import {
  AnalyticsFieldName,
  GroupSectionAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import {
  GroupRegistrationError,
  GroupRegistrationText,
} from "@/constants/Schedule";
import { AnimationDuration } from "@/constants/UI";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import { captureException } from "@/services/AppMonitor";
import { useUpdateGroupSectionRegistrationMutation } from "@/store/api/schedule/groupClasses";
import type { ToastRequest } from "@/ui/composed/toast/Toast";
import type { GroupSessionCardItem } from "@/utils/groupClassV2.util";

const ErrorToast: ToastRequest = {
  text: GroupRegistrationText.RegistrationGenericError,
  variant: "error",
};

// reports a register/unregister failure to Datadog with the ids needed to trace it
const captureRegistrationError = ({
  cause,
  groupId,
  groupSectionId,
  isRegistered,
  patientId,
}: {
  cause: unknown;
  groupId: string;
  groupSectionId?: string;
  isRegistered: boolean;
  patientId: string;
}) =>
  captureException(
    new Error(
      isRegistered
        ? GroupRegistrationError.RegisterError
        : GroupRegistrationError.UnregisterError,
      { cause },
    ),
    { context: { groupId, groupSectionId, patientId } },
  );

interface TrackFlowEventArgs {
  event: string;
  groupSectionId?: string;
  // only set by the register events: unregistering from the edit sheet is a cancellation,
  // not a section switch, so the property is omitted there rather than reported as true
  isSwitchingSection?: boolean;
}

interface UseGroupRegistrationFlowProps {
  groupId: string;
  onError?: () => void;
  patientId: string;
  registeredSection?: GroupSessionCardItem;
  sessions: GroupSessionCardItem[];
  // the surface the user entered the details screen from (search list, carousel, deep link,
  // schedule event). Reported on every event this flow fires, so registrations stay
  // attributable to their entry point the way the v1 "Register pressed" event was
  source: GroupClassSeriesAnalyticsSources;
}

interface UseGroupRegistrationFlow {
  closeEdit: () => void;
  closeUnregister: () => void;
  deselectSession: () => void;
  flushPendingToast: () => void;
  isEditOpen: boolean;
  isLoading: boolean;
  isUnregisterOpen: boolean;
  onConfirmRegister: () => void;
  onConfirmUnregister: () => void;
  openEdit: () => void;
  openUnregister: () => void;
  selectedSession?: GroupSessionCardItem;
  selectSession: (groupSectionId: string) => void;
}

// orchestrates the register / edit / unregister sheet flow for the v2 group details screen
const useGroupRegistrationFlow = ({
  groupId,
  onError,
  patientId,
  registeredSection,
  sessions,
  source,
}: UseGroupRegistrationFlowProps): UseGroupRegistrationFlow => {
  const { showToast } = useToast();
  const { track } = useAnalytics();
  const [updateGroupSectionRegistration, { isLoading }] =
    useUpdateGroupSectionRegistrationMutation();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUnregisterOpen, setIsUnregisterOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string>();
  const selectedSession = isNil(selectedSessionId)
    ? undefined
    : sessions.find((s) => s.id === selectedSessionId);

  // sheets render above this screen's toast layer, so the toast is stashed and shown
  // from onDismissAnimationEnd once the sheet has fully closed
  const [pendingToast, setPendingToast] = useState<ToastRequest>();

  const trackFlowEvent = ({
    event,
    groupSectionId,
    isSwitchingSection,
  }: TrackFlowEventArgs) =>
    track(event, {
      [AnalyticsFieldName.GroupClassId]: groupId,
      [AnalyticsFieldName.PatientId]: patientId,
      [AnalyticsFieldName.Source]: source,
      ...(isNil(groupSectionId)
        ? {}
        : { [AnalyticsFieldName.GroupSectionId]: groupSectionId }),
      ...(isNil(isSwitchingSection)
        ? {}
        : { [AnalyticsFieldName.IsSwitchingSection]: isSwitchingSection }),
    });

  // resolves to true on success, false on failure, so callers can react to the outcome
  const register = ({
    groupSectionId,
    isRegistered,
  }: {
    groupSectionId: string;
    isRegistered: boolean;
  }): Promise<boolean> =>
    updateGroupSectionRegistration({
      groupSectionId,
      isRegistered,
      patientExternalId: patientId,
    })
      .unwrap()
      .then(() => {
        trackFlowEvent({
          event: isRegistered
            ? GroupSectionAnalyticsEvent.RegisterSucceeded
            : GroupSectionAnalyticsEvent.UnregisterSucceeded,
          groupSectionId,
          isSwitchingSection: isRegistered ? isEditOpen : undefined,
        });
        setPendingToast({
          text: isRegistered
            ? GroupRegistrationText.Registered
            : GroupRegistrationText.Unregistered,
          variant: "success",
        });
        return true;
      })
      .catch((error) => {
        captureRegistrationError({
          cause: error,
          groupId,
          groupSectionId,
          isRegistered,
          patientId,
        });
        trackFlowEvent({
          event: isRegistered
            ? GroupSectionAnalyticsEvent.RegisterFailed
            : GroupSectionAnalyticsEvent.UnregisterFailed,
          groupSectionId,
          isSwitchingSection: isRegistered ? isEditOpen : undefined,
        });
        // on error, dismiss the details screen immediately; show the toast now (rather than
        // deferring to a sheet's onDismissAnimationEnd) since the screen is unmounting
        onError?.();
        setTimeout(() => showToast(ErrorToast), AnimationDuration.Default);
        return false;
      });

  const onConfirmRegister = () => {
    if (isNil(selectedSessionId)) return;
    trackFlowEvent({
      event: GroupSectionAnalyticsEvent.ConfirmRegisterPressed,
      groupSectionId: selectedSessionId,
      // the edit sheet is only open when the user got here by switching sections on a group
      // they were already registered for, rather than registering fresh
      isSwitchingSection: isEditOpen,
    });
    void register({
      groupSectionId: selectedSessionId,
      isRegistered: true,
    }).finally(() => {
      // close the confirm sheet and (when launched from the edit sheet) the edit sheet;
      // whichever closes last flushes the success/error toast via onDismissAnimationEnd
      setSelectedSessionId(undefined);
      setIsEditOpen(false);
    });
  };

  const onConfirmUnregister = () => {
    setIsUnregisterOpen(false);
    if (isNil(registeredSection)) return;
    trackFlowEvent({
      event: GroupSectionAnalyticsEvent.ConfirmUnregisterPressed,
      groupSectionId: registeredSection.id,
    });
    void register({
      groupSectionId: registeredSection.id,
      isRegistered: false,
      // close the edit sheet on success AND failure so the toast (registered/error) shows
    }).finally(() => setIsEditOpen(false));
  };

  const flushPendingToast = () => {
    if (isNil(pendingToast)) return;
    showToast(pendingToast);
    setPendingToast(undefined);
  };

  const openEdit = () => {
    trackFlowEvent({
      event: GroupSectionAnalyticsEvent.EditRegistrationPressed,
    });
    setIsEditOpen(true);
  };

  // opening the confirm sheet for a session counts as selecting it
  const selectSession = (groupSectionId: string) => {
    trackFlowEvent({
      event: GroupSectionAnalyticsEvent.SessionSelected,
      groupSectionId,
    });
    setSelectedSessionId(groupSectionId);
  };

  return {
    closeEdit: () => setIsEditOpen(false),
    closeUnregister: () => setIsUnregisterOpen(false),
    deselectSession: () => setSelectedSessionId(undefined),
    flushPendingToast,
    isEditOpen,
    isLoading,
    isUnregisterOpen,
    onConfirmRegister,
    onConfirmUnregister,
    openEdit,
    openUnregister: () => setIsUnregisterOpen(true),
    selectedSession,
    selectSession,
  };
};

export default useGroupRegistrationFlow;
